import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { APPS_NAV_LINK } from 'src/content/MiniExperts/constants';
import type {
  MeetingSummary,
  NoteItem,
  SmartMeetContextType,
  SpeakerActionItem
} from 'src/content/MiniExperts/SmartMeet/types/types';
import {
  getAudioDuration,
  getAudioFile,
  getNewMeetingName,
  MAX_DURATION_SECONDS
} from 'src/content/MiniExperts/SmartMeet/utils/utils';
import { useAlert } from 'src/hooks/useAlert';
import { useMicrophonePermission } from 'src/hooks/useMicrophonePermission';
import {
  addNote,
  incrementRefreshKey,
  pauseRecording,
  RecordingProgress,
  resumeRecording,
  setDraftMeeting,
  setMeetingData,
  setNotes,
  setProgress,
  startRecordingAsync,
  stopRecordingAsync
} from 'src/redux/slices/smartMeet';
import { useDispatch, useSelector } from 'src/redux/store';
import type { IBreadcrumbItem } from 'src/types/custom_component';
import { ALERT } from 'src/types/enum';
import { MAX_NAME_LENGTH } from 'src/utils/constants';
import logger from 'src/utils/logger';
import { getMeetingMetadata, saveMeetingMetadata } from 'src/utils/recordingDB';
import { TRANSLATION_CONSTANTS as T } from 'src/utils/translations';

import { useCreateMeetHandler } from './useCreateMeetHandler';
import { useSmartMeetService } from './useSmartMeetService';
import { useSmartMeetWebSocket } from './useSmartMeetWebSocket';

const SmartMeetContext = createContext<SmartMeetContextType | null>(null);

export const SmartMeetProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showAlert } = useAlert();
  const { t } = useTranslation();

  const {
    isRecording,
    meetingData,
    notes,
    draftMeeting,
    audioURL,
    isPaused,
    errorMsg
  } = useSelector((state) => state.smartMeet);

  const { uploadMeetingAudio } = useCreateMeetHandler();
  const { deleteSmartMeet, updateSmartMeet, createNewMeeting } =
    useSmartMeetService();
  useSmartMeetWebSocket();
  const { ensureMicrophoneAccess } = useMicrophonePermission();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  const refreshGrid = () => dispatch(incrementRefreshKey());

  const initialSummaryVersions: MeetingSummary[] = Array.isArray(
    meetingData?.settings?.meeting_summary
  )
    ? meetingData.settings.meeting_summary
    : [];
  const [summaryVersions, setSummaryVersions] = useState<MeetingSummary[]>(
    initialSummaryVersions
  );
  const [currentVersionIndex, setCurrentVersionIndex] = useState(0);

  const onBreadcrumbItemClick = (item: IBreadcrumbItem) => {
    if (item.webUrl) {
      refreshGrid();
      navigate(item.webUrl);
      setIsEditing(false);
    }
  };

  const startRecording = async () => {
    const hasAccess = await ensureMicrophoneAccess();
    if (!hasAccess) {
      return;
    }

    setIsUpdating(true);
    const name = getNewMeetingName();
    try {
      const data = await createNewMeeting(name, {
        add_notes: false,
        duration: 0
      });
      setIsUpdating(false);
      if (!data) {
        return;
      }

      const meetingData = data?.data?.data?.[0];
      const meetingName = `${meetingData?.id}-${meetingData?.tenant_id}`;
      dispatch(setDraftMeeting(meetingData));

      const result = await (dispatch as any)(startRecordingAsync(meetingName));
      if (startRecordingAsync.rejected.match(result)) {
        if (result.payload === 'INDEXEDDB_UNAVAILABLE') {
          showAlert(ALERT.ERROR, t(T.storageUnavailableError));
          await deleteSmartMeet(meetingData?.id);
        }
        dispatch(setDraftMeeting(null));
        return;
      }

      dispatch(setNotes([]));
    } catch {
      setIsUpdating(false);
    }
  };

  const stopRecording = async (meetingName?: string) => {
    try {
      const result = await dispatch(stopRecordingAsync());
      if (!meetingName) {
        try {
          meetingName = result?.payload?.meetingName;
        } catch (err) {
          showAlert(ALERT.ERROR, t(T.stopRecordingError));
          logger.error('Error stopping recording:', err);
          return;
        } finally {
          dispatch(
            setProgress({
              status_code: RecordingProgress.StoppingAudio
            })
          );
        }
      }

      const meetingId = Number(meetingName.split('-')?.[0]);

      const audioFile = await getAudioFile(meetingName);
      const normalizedType = audioFile?.type?.split(';')?.[0];
      const duration = await getAudioDuration(audioFile);

      const uploadFile = new File([audioFile], audioFile?.name, {
        type: normalizedType,
        lastModified: Date.now()
      });
      await uploadMeetingAudio(uploadFile, {
        duration,
        fileName: draftMeeting?.name,
        meetingId
      });
    } catch (err) {
      logger.error('Error stopping recording:', err);
    }
  };

  const handlePauseResumeAction = async () => {
    if (isPaused) {
      const hasAccess = await ensureMicrophoneAccess();
      if (!hasAccess) {
        return;
      }
      dispatch(resumeRecording());
    } else {
      dispatch(pauseRecording());
    }
  };

  const closeDrawer = () => setIsDrawerOpen(false);
  const openDrawer = () => setIsDrawerOpen(true);

  useEffect(() => {
    if (!meetingData?.id) {
      return;
    }

    if (isRecording) {
      if (draftMeeting?.id && draftMeeting?.tenant_id) {
        const meetingName = `${draftMeeting.id}-${draftMeeting.tenant_id}`;
        getMeetingMetadata(meetingName)
          .then((metadata) => {
            if (metadata?.notes?.length) {
              dispatch(setNotes(metadata.notes));
            }
          })
          .catch((err) => {
            logger.error('Error restoring notes from IndexedDB:', err);
          });
      }
    } else {
      dispatch(setNotes(meetingData.settings?.notes?.items || []));
    }

    const summary = meetingData.settings?.meeting_summary;
    if (summary) {
      setSummaryVersions(Array.isArray(summary) ? summary : [summary]);
      setCurrentVersionIndex(0);
    }
  }, [dispatch, meetingData, isRecording, draftMeeting]);

  useEffect(() => {
    if (!isRecording) {
      return;
    }

    let elapsed = 0;

    const timer = setInterval(() => {
      elapsed += 1;

      if (elapsed >= MAX_DURATION_SECONDS) {
        clearInterval(timer);
        logger.warn('Recording reached 2-hour limit, stopping automatically.');
        void stopRecording();
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [isRecording]);

  const handleAddNote = async (note: NoteItem) => {
    if (isRecording) {
      dispatch(addNote(note));

      // Persist notes to IndexedDB so they survive page reload/close
      if (draftMeeting?.id && draftMeeting?.tenant_id) {
        const meetingName = `${draftMeeting.id}-${draftMeeting.tenant_id}`;
        const updatedNotes = [...(notes ?? []), note];
        saveMeetingMetadata(meetingName, { notes: updatedNotes }).catch(
          (err) => {
            logger.error('Error persisting notes to IndexedDB:', err);
          }
        );
      }
      return;
    }
    if (!meetingData?.id) {
      return;
    }
    let existingNotes: string[] = [];
    // convert string to object when needed
    try {
      const raw = meetingData?.settings?.notes;

      if (!raw) {
        existingNotes = [];
      } else if (Array.isArray(raw)) {
        existingNotes = raw;
      } else if (typeof raw === 'object') {
        existingNotes = raw.items || [];
      } else if (typeof raw === 'string') {
        const parsed = JSON.parse(raw);
        existingNotes = parsed.items || [];
      }
    } catch (err) {
      logger.error('Error parsing notes:', err);
      existingNotes = [];
    }
    const updatedNotes = [...existingNotes, note];
    await updateMeetingField({ notes: updatedNotes });
  };

  const updateMeetingField = async (fields: Record<string, any>) => {
    if (!meetingData?.id) {
      return;
    }
    setIsUpdating(true);

    const payload: any = { id: meetingData.id };
    const updatedRoot: Record<string, any> = {};
    const updatedSettings: Record<string, any> = {};

    for (const key in fields) {
      const value = fields[key];

      if (key === 'notes' || key === 'action_items') {
        payload[key] = JSON.stringify({ items: value });
        updatedSettings[key] = { items: value };
      } else if (key === 'summary') {
        payload.summary = JSON.stringify(value);
        updatedSettings.meeting_summary = value;
      } else {
        payload[key] = value;
        updatedRoot[key] = value;
      }
    }

    dispatch(
      setMeetingData({
        ...meetingData,
        ...updatedRoot,
        settings: {
          ...meetingData.settings,
          ...updatedSettings
        }
      })
    );

    try {
      await updateSmartMeet(meetingData.id, payload);
    } catch (error) {
      logger.error(`Error updating ${Object.keys(fields).join(', ')}:`, error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleNameUpdate = async (newName: string) => {
    if (!meetingData?.id) {
      return;
    }

    const trimmedName = newName?.trim();
    if (!trimmedName) {
      showAlert(ALERT.ERROR, t(T.emptySpaceError));
      return;
    }
    if (trimmedName.length > MAX_NAME_LENGTH) {
      showAlert(ALERT.ERROR, t(T.nameCharacterLengthError));
      return;
    }

    await updateMeetingField({ name: trimmedName });
  };

  const handleInlineEditNameUpdate = async (
    id: number,
    newName: string
  ): Promise<boolean> => {
    if (!id) {
      return false;
    }

    const trimmedName = newName?.trim();
    if (!trimmedName) {
      showAlert(ALERT.ERROR, t(T.emptySpaceError));
      return false;
    }
    if (trimmedName.length > MAX_NAME_LENGTH) {
      showAlert(ALERT.ERROR, t(T.nameCharacterLengthError));
      return false;
    }

    const payload: any = { id, name: trimmedName };

    try {
      const result = await updateSmartMeet(id, payload);
      if (result === undefined) {
        return false;
      }
      refreshGrid();
      return true;
    } catch (error) {
      logger.error(`Error updating name:`, error);
      return false;
    }
  };

  const handleSaveSummary = async () => {
    if (!meetingData) {
      return;
    }
    setIsEditing(false);

    const currentSummary = summaryVersions[currentVersionIndex];

    await updateMeetingField({
      summary: currentSummary
    });

    setCurrentVersionIndex(0);
  };

  const handleActionItemsUpdate = async (
    updatedActionItems: SpeakerActionItem[]
  ) => {
    await updateMeetingField({ action_items: updatedActionItems });
  };

  const addNewSummaryVersion = (summary: MeetingSummary) => {
    const updatedVersions = [...summaryVersions, summary];
    setSummaryVersions(updatedVersions);
    setCurrentVersionIndex(updatedVersions.length - 1);
  };

  const handleSummaryVersionChange = (direction: 'next' | 'prev') => () => {
    const step = direction === 'next' ? 1 : -1;
    const newIndex = currentVersionIndex + step;
    if (newIndex >= 0 && newIndex < summaryVersions.length) {
      setCurrentVersionIndex(newIndex);
    }
  };

  const handleSummaryCancel = () => {
    setIsEditing(false);
    setSummaryVersions([summaryVersions[0]]);
    setCurrentVersionIndex(0);
  };

  const handleCleanSummaryVersions = () => {
    setSummaryVersions([]);
    setCurrentVersionIndex(0);
  };

  const onRowClick = async (meetingId: number) => {
    navigate(`${APPS_NAV_LINK.SMART_MEET}/${meetingId}`);
  };

  const deleteMeetingById = async (meetingId: number) => {
    try {
      await deleteSmartMeet(meetingId);
      refreshGrid();
    } catch (error) {
      logger.error('Error deleting meeting:', error);
      throw error;
    }
  };

  return (
    <SmartMeetContext.Provider
      value={{
        isPaused,
        isUpdating,
        isEditing,
        isRecording,
        isDrawerOpen,
        notes,
        errorMsg,
        audioURL,
        meetingData,
        currentVersionIndex,
        summaryVersions,
        handleNameUpdate,
        deleteMeetingById,
        addNewSummaryVersion,
        handleSaveSummary,
        handleSummaryVersionChange,
        closeDrawer,
        openDrawer,
        onBreadcrumbItemClick,
        startRecording,
        stopRecording,
        handlePauseResumeAction,
        onRowClick,
        setIsEditing,
        handleAddNote,
        handleSummaryCancel,
        handleActionItemsUpdate,
        handleInlineEditNameUpdate,
        handleCleanSummaryVersions,
        refreshGrid
      }}
    >
      {children}
    </SmartMeetContext.Provider>
  );
};

export const useSmartMeet = () => {
  const context = useContext(SmartMeetContext);
  if (!context) {
    logger.warn('useSmartMeet called outside SmartMeetProvider');
    return {} as SmartMeetContextType;
  }
  return context;
};
